import { memo } from 'react';

import { UpsellFlashIconButton } from '~/components/SharePrivateClipModalV2/UpsellFlashIconButton';
import { SharePermissionSection } from '~/components/SharePrivateContentModal/SharePrivateContentSettings/SharePermissionSection';
import { type SharePermissionSectionContentProps } from '~/components/SharePrivateContentModal/SharePrivateContentSettings/types';
import { SharePrivateContentPermissionToggle } from '~/components/SharePrivateContentModal/shared/SharePrivateContentPermissionToggle';
import { SHARE_MODAL_UPSELL_MESSAGE } from '~/constants/upsells';
import { useShareSettings } from '~/hooks/useShareSettings';

type ShareCustomFieldsPermissionsProps = {
  canViewCustomFields: boolean;
  canEditCustomFields: boolean;
} & SharePermissionSectionContentProps;

export const ShareCustomFieldsPermissions = memo(
  ({
    onPermissionUpdated,
    shortIdInfo,
    isLoading,
    canViewCustomFields,
    canEditCustomFields,
  }: ShareCustomFieldsPermissionsProps) => {
    const { customFieldsEnabled } = useShareSettings();

    return (
      <SharePermissionSection title="Custom fields">
        <SharePrivateContentPermissionToggle
          label="Show custom fields"
          id="customField.canView"
          isLoading={isLoading}
          checked={shortIdInfo.permissions.customField.canView}
          onCheckedChange={(checked) =>
            onPermissionUpdated({
              customField: {
                canView: checked,
                canManageApplications: checked ? shortIdInfo.permissions.customField.canManageApplications : false,
              },
            })
          }
          disabled={
            !canViewCustomFields ||
            (shortIdInfo.permissions.customField.canManageApplications && !canEditCustomFields) ||
            !customFieldsEnabled
          }
        >
          {!customFieldsEnabled && <UpsellFlashIconButton>{SHARE_MODAL_UPSELL_MESSAGE}</UpsellFlashIconButton>}
        </SharePrivateContentPermissionToggle>
        <SharePrivateContentPermissionToggle
          label="Allow editing custom fields"
          id="customField.canManageApplications"
          isLoading={isLoading}
          checked={shortIdInfo.permissions.customField.canManageApplications}
          onCheckedChange={(checked) =>
            onPermissionUpdated({
              customField: {
                canManageApplications: checked,
              },
            })
          }
          disabled={!canEditCustomFields || !shortIdInfo.permissions.customField.canView || !customFieldsEnabled}
        >
          {!customFieldsEnabled && <UpsellFlashIconButton>{SHARE_MODAL_UPSELL_MESSAGE}</UpsellFlashIconButton>}
        </SharePrivateContentPermissionToggle>
      </SharePermissionSection>
    );
  },
);

ShareCustomFieldsPermissions.displayName = 'ShareCustomFieldsPermissions';
